import { useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query';
import {
  fetchHome,
  fetchHomeFacets,
  fetchHomePoints,
  type HomePointsResponse,
  fetchHomeNeighbours,
  fetchHomeSignals,
  type HomeSignalsResponse,
  fetchHomes,
  type HomeFacetsResponse,
  type HomeLookup,
  type HomeNeighboursResponse,
  type HomeRow,
  type HomeTotals,
  type HomesListResponse,
  type HomesQuery,
} from './client.js';

// React-query hooks over the care-home grain endpoints (GET /homes*). Mirrors
// screener-hooks: offset-paged infinite list for the virtualised table, plus
// facets / one home / neighbours.

// Lazy-loaded on scroll like Triage. Home rows are narrow (only the visible
// columns are requested), so a 300-row page fills the viewport with headroom.
const PAGE_SIZE = 300;

export function useInfiniteHomes(query: HomesQuery) {
  return useInfiniteQuery<
    HomesListResponse,
    Error,
    InfiniteData<HomesListResponse>,
    readonly [string, HomesQuery],
    number
  >({
    queryKey: ['homes', query] as const,
    queryFn: ({ pageParam }) => fetchHomes({ ...query, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const consumed = lastPage.offset + lastPage.rows.length;
      return consumed < lastPage.total ? consumed : undefined;
    },
    staleTime: 30_000,
    // Keep the previous result on screen while a filter / sort change refetches.
    placeholderData: (prev) => prev,
  });
}

export function flattenHomePages(data: InfiniteData<HomesListResponse> | undefined): HomeRow[] {
  if (!data) return [];
  const out: HomeRow[] = [];
  for (const page of data.pages) for (const r of page.rows) out.push(r);
  return out;
}

/** Totals for the filtered set — from the first page (every page repeats them). */
export function totalsFromHomePages(
  data: InfiniteData<HomesListResponse> | undefined,
): HomeTotals | null {
  return data?.pages[0]?.totals ?? null;
}

/** Facet counts — fetched only while `enabled` (the filter modal is open): one
 *  GROUP BY per facet over the whole index is too heavy to run on every
 *  filter change nobody is looking at. */
export function useHomeFacets(query: HomesQuery, enabled: boolean) {
  return useQuery<HomeFacetsResponse>({
    queryKey: ['homes', 'facets', query],
    queryFn: () => fetchHomeFacets(query),
    enabled,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Home pins for the Map (home mode) — the same filters as the linked Homes screener. */
export function useHomePoints(query: HomesQuery, enabled: boolean) {
  return useQuery<HomePointsResponse>({
    queryKey: ['homes', 'points', query],
    queryFn: () => fetchHomePoints(query),
    enabled,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

export function useHome(homeKey: string | null) {
  return useQuery<HomeLookup>({
    queryKey: ['homes', 'one', homeKey],
    queryFn: () => fetchHome(homeKey!),
    enabled: !!homeKey,
    staleTime: 5 * 60_000,
  });
}

export function useHomeNeighbours(homeKey: string | null, radiusKm: number) {
  return useQuery<HomeNeighboursResponse>({
    queryKey: ['homes', 'neighbours', homeKey, radiusKm],
    queryFn: () => fetchHomeNeighbours(homeKey!, radiusKm),
    enabled: !!homeKey,
    staleTime: 5 * 60_000,
  });
}

/** A home's current quality signals (Home record, Quality tab). */
export function useHomeSignals(homeKey: string | null) {
  return useQuery<HomeSignalsResponse>({
    queryKey: ['homes', 'signals', homeKey],
    queryFn: () => fetchHomeSignals(homeKey!),
    enabled: !!homeKey,
    staleTime: 5 * 60_000,
  });
}
